import { ContactsInput } from "@/components/contacts";
import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Kv } from "@/components/ui/kv";
import { PhoneInput } from "@/components/ui/phone";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { TimeRange } from "@/components/ui/time-range";
import { useGetRegions } from "@/hooks/regions";
import {
  CreateHotel,
  HotelCategoriesAsArray,
  HotelCategory,
  HotelPriority,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
}: {
  model?: CreateHotel;
  onUpsert: (data: CreateHotel) => Promise<void>;
}) {
  const { data: regions, isLoading } = useGetRegions();
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateHotel>>(() => {
    const config: FormOptions<CreateHotel> = {
      year: {
        value: model?.year ?? new Date().getFullYear(),
      },
      name: {
        value: model?.name ?? "",
        validator: z.string().min(1, "errors.required"),
      },
      address: {
        value: model?.address ?? "",
      },
      zipCode: {
        value: model?.zipCode ?? "",
      },
      phoneNumber: {
        value: model?.phoneNumber ?? "",
      },

      contacts: {
        value: model?.contacts ?? [],
      },
      email: {
        value: model?.contacts?.find(x => x.email != undefined)?.email ,
      },
      socialReason: {
        value: model?.socialReason ?? "",
      },
      vat: {
        value: model?.vat ?? "",
      },
      iban: {
        value: model?.iban ?? "",
      },
      regionId: {
        value: model?.regionId,
        validator: z.number({
          required_error: "errors.required",
        }),
      },
      locality: {
        value: model?.locality ?? "",
      },
      priority: {
        value: model?.priority ?? "",
      },
      category: {
        value: model?.category,
        validator: z
          .nativeEnum(HotelCategory, {
            required_error: "errors.required",
          })
          .nullish(),
      },
      bikeStorage: {
        value: model?.bikeStorage ?? false,
      },
      bikeStoragePrice: {
        value: model?.bikeStoragePrice,
      },
      bikeStorageQuantity: {
        value: model?.bikeStorageQuantity,
      },
      paymentAfterInvoice: {
        value: model?.paymentAfterInvoice ?? false,
      },
      kidFriendly: {
        value: model?.kidFriendly ?? false,
      },
      petFriendly: {
        value: model?.petFriendly ?? false,
      },
      childMaxAge: {
        value: model?.childMaxAge,
      },
      hasPicnic: {
        value: model?.hasPicnic ?? false,
      },
      picnicPrice: {
        value: model?.picnicPrice,
      },
      checkInTime: {
        value: model?.checkInTime,
      },
      checkOutTime: {
        value: model?.checkOutTime,
      },
      breakfastStartTime: {
        value: model?.breakfastStartTime,
      },
      breakfastEndTime: {
        value: model?.breakfastEndTime,
      },
      dinnerAutoScheduling: {
        value: model?.dinnerAutoScheduling ?? false,
      },
      hasDinner: {
        value: model?.hasDinner ?? false,
      },
      dinnerPrice: {
        value: model?.dinnerPrice,
      },
      hasBar: {
        value: model?.hasBar ?? false,
      },
      group: {
        value: model?.group ?? "",
      },
      bookingDiscount: {
        value: model?.bookingDiscount,
      },
      messageChainId: {
        value: model?.messageChainId ?? "",
      },
      picnicNotes: {
        value: model?.picnicNotes,
      },
      bikeNotes: {
        value: model?.bikeNotes,
      },
      dinnerNotes: {
        value: model?.dinnerNotes,
      },
      petNotes: {
        value: model?.petNotes,
      },
      externalObservations: {
        value: model?.externalObservations,
      },
      internalObservations: {
        value: model?.internalObservations,
      },
      bar: {
        value: model?.bar,
      },
    };
    return config;
  }, [model]);

  return (
    <DomainUpsert
      title={model?.name ?? t("hotels.new.title")}
      buttonTranslation={isEdit ? "hotels.update.button" : "hotels.new.button"}
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "name",
          label: "hotels.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name ?? ""}
              onChange={(e) => {
                form.setValue("name", e.target.value);
              }}
            />
          ),
        },
        {
          key: "socialReason",
          label: "hotels.fields.socialReason",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.socialReason ?? ""}
              onChange={(e) => {
                form.setValue("socialReason", e.target.value);
              }}
            />
          ),
        },
        {
          key: "group",
          label: "hotels.fields.group",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.group ?? ""}
              onChange={(e) => {
                form.setValue("group", e.target.value);
              }}
            />
          ),
        },
        {
          key: "category",
          label: "hotels.fields.category",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.category ?? undefined}
              onChange={(v) => {
                form.setValue("category", v);
              }}
              options={
                HotelCategoriesAsArray?.map((v) => ({
                  value: v,
                  label: t(`hotels.categories.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "priority",
          label: "hotels.fields.priority",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.priority ?? undefined}
              onChange={(v) => {
                form.setValue("priority", v);
              }}
              options={
                HotelPriority?.map((v) => ({
                  value: v,
                  label: v,
                })) ?? []
              }
            />
          ),
        },
        {
          key: "address",
          label: "hotels.fields.address",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.address ?? ""}
              onChange={(e) => {
                form.setValue("address", e.target.value);
              }}
            />
          ),
        },
        {
          key: "zipCode",
          label: "hotels.fields.zipCode",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.zipCode ?? ""}
              onChange={(e) => {
                form.setValue("zipCode", e.target.value);
              }}
            />
          ),
        },
        {
          key: "locality",
          label: "hotels.fields.locality",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.locality ?? ""}
              onChange={(e) => {
                form.setValue("locality", e.target.value);
              }}
            />
          ),
        },
        {
          key: "regionId",
          label: "hotels.fields.regionId",
          input: (form, id) =>
            isLoading || !regions ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.regionId?.toString() ?? undefined}
                onChange={(v) => {
                  form.setValue("regionId", parseInt(v));
                }}
                options={
                  regions?.map((v) => ({
                    value: v.id,
                    label: v.name,
                  })) ?? []
                }
              />
            ),
        },
        {
          key: "phoneNumber",
          label: "hotels.fields.phoneNumber",
          input: (form, id) => (
            <PhoneInput
              className="w-full"
              id={id}
              value={form.value.phoneNumber ?? ""}
              onChange={(value) => {
                form.setValue("phoneNumber", value);
              }}
            />
          ),
        },
        {
          key: "contacts",
          label: "hotels.fields.contacts",
          input: (form, id) => (
            <ContactsInput
              id={id}
              contacts={form.value.contacts ?? []}
              onChange={(v) => {
                form.setValue("contacts", v);
                form.setValue("email", v?.find(x => x.email != undefined)?.email);
              }}
            />
          ),
        },
        {
          key: "vat",
          label: "hotels.fields.vat",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.vat ?? ""}
              onChange={(e) => {
                form.setValue("vat", e.target.value);
              }}
            />
          ),
        },
        {
          key: "paymentAfterInvoice",
          label: "hotels.fields.paymentAfterInvoice",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.paymentAfterInvoice}
              onChange={(v) => {
                form.setValue("paymentAfterInvoice", v);
              }}
              options={
                [true, false].map((v) => ({
                  value: v,
                  label: t(`common.boolean.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "hasBar",
          label: "hotels.fields.hasBar",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("hasBar", v);
              }}
              checked={!!form.value.hasBar}
            />
          ),
        },
        {
          visible: (form) => !!form.value.hasBar,
          key: "bar",
          label: "hotels.fields.bar",
          input: (form, id) => (
            <Kv
              id={id}
              value={form.value.bar ?? {}}
              onChange={(v) => {
                form.setValue("bar", v);
              }}
            />
          ),
        },
        {
          key: "bookingDiscount",
          label: "hotels.fields.bookingDiscount",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              value={form.value.bookingDiscount ?? ""}
              onChange={(e) => {
                form.setValue(
                  "bookingDiscount",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "iban",
          label: "hotels.fields.iban",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.iban ?? ""}
              onChange={(e) => {
                form.setValue("iban", e.target.value);
              }}
            />
          ),
        },
        {
          key: "checkInTime",
          hideErrors: true,
          label: null,
          input: (form) => (
            <TimeRange
              idStart="checkInTime"
              start={form.value.checkInTime ?? ""}
              startLabel="hotels.fields.checkInTime"
              startErrors={form.errors.checkInTime}
              onStartChange={(v) => {
                form.setValue("checkInTime", v);
              }}
              idEnd="checkOutTime"
              endLabel="hotels.fields.checkOutTime"
              endErrors={form.errors.checkOutTime}
              end={form.value.checkOutTime ?? ""}
              onEndChange={(v) => {
                form.setValue("checkOutTime", v);
              }}
            />
          ),
        },
        {
          key: "breakfastStartTime",
          hideErrors: true,
          label: null,
          input: (form) => (
            <TimeRange
              idStart="breakfastStartTime"
              start={form.value.breakfastStartTime ?? ""}
              startLabel="hotels.fields.breakfastStartTime"
              startErrors={form.errors.breakfastStartTime}
              onStartChange={(v) => {
                form.setValue("breakfastStartTime", v);
              }}
              idEnd="breakfastEndTime"
              endLabel="hotels.fields.breakfastEndTime"
              endErrors={form.errors.breakfastEndTime}
              end={form.value.breakfastEndTime ?? ""}
              onEndChange={(v) => {
                form.setValue("breakfastEndTime", v);
              }}
            />
          ),
        },
        {
          key: "hasPicnic",
          label: "hotels.fields.hasPicnic",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("hasPicnic", v);
              }}
              checked={!!form.value.hasPicnic}
            />
          ),
        },
        {
          visible: (form) => !!form.value.hasPicnic,
          key: "picnicPrice",
          label: "hotels.fields.picnicPrice",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              value={form.value.picnicPrice ?? ""}
              onChange={(e) => {
                form.setValue("picnicPrice", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          visible: (form) => !!form.value.hasPicnic,
          key: "picnicNotes",
          label: "hotels.fields.picnicNotes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.picnicNotes ?? ""}
              onChange={(e) => {
                form.setValue("picnicNotes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "hasDinner",
          label: "hotels.fields.hasDinner",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("hasDinner", v);
              }}
              checked={!!form.value.hasDinner}
            />
          ),
        },
        {
          visible: (form) => !!form.value.hasDinner,
          key: "dinnerPrice",
          label: "hotels.fields.dinnerPrice",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              value={form.value.dinnerPrice ?? ""}
              onChange={(e) => {
                form.setValue("dinnerPrice", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          visible: (form) => !!form.value.hasDinner,
          key: "dinnerAutoScheduling",
          label: "hotels.fields.dinnerAutoScheduling",
          orientation: "horizontal",
          input: (form, id) => (
            <Checkbox
              id={id}
              className="ml-2"
              onCheckedChange={(v) => {
                form.setValue(
                  "dinnerAutoScheduling",
                  v === "indeterminate" ? false : v,
                );
              }}
              checked={!!form.value.dinnerAutoScheduling}
            />
          ),
        },
        {
          visible: (form) => !!form.value.hasDinner,
          key: "dinnerNotes",
          label: "hotels.fields.dinnerNotes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.dinnerNotes ?? ""}
              onChange={(e) => {
                form.setValue("dinnerNotes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "bikeStorage",
          label: "hotels.fields.bikeStorage",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("bikeStorage", v);
              }}
              checked={!!form.value.bikeStorage}
            />
          ),
        },
        {
          visible: (form) => !!form.value.bikeStorage,
          key: "bikeStoragePrice",
          label: "hotels.fields.bikeStoragePrice",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              value={form.value.bikeStoragePrice ?? ""}
              onChange={(e) => {
                form.setValue(
                  "bikeStoragePrice",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          visible: (form) => !!form.value.bikeStorage,
          key: "bikeStorageQuantity",
          label: "hotels.fields.bikeStorageQuantity",
          input: (form, id) => (
            <Input
              id={id}
              min={0}
              step={1}
              type="number"
              value={form.value.bikeStorageQuantity ?? ""}
              onChange={(e) => {
                form.setValue("bikeStorageQuantity", parseInt(e.target.value));
              }}
            />
          ),
        },
        {
          visible: (form) => !!form.value.bikeStorage,
          key: "bikeNotes",
          label: "hotels.fields.bikeNotes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.bikeNotes ?? ""}
              onChange={(e) => {
                form.setValue("bikeNotes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "petFriendly",
          label: "hotels.fields.petFriendly",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("petFriendly", v);
              }}
              checked={!!form.value.petFriendly}
            />
          ),
        },
        {
          visible: (form) => !!form.value.petFriendly,
          key: "petNotes",
          label: "hotels.fields.petNotes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.petNotes ?? ""}
              onChange={(e) => {
                form.setValue("petNotes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "kidFriendly",
          label: "hotels.fields.kidFriendly",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("kidFriendly", v);
              }}
              checked={!!form.value.kidFriendly}
            />
          ),
        },
        {
          key: "childMaxAge",
          label: "hotels.fields.childMaxAge",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              value={form.value.childMaxAge ?? ""}
              onChange={(e) => {
                form.setValue("childMaxAge", parseInt(e.target.value));
              }}
            />
          ),
        },
        {
          key: "internalObservations",
          label: "hotels.fields.internalObservations",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.internalObservations ?? ""}
              onChange={(e) => {
                form.setValue("internalObservations", e.target.value);
              }}
            />
          ),
        },
        {
          key: "externalObservations",
          label: "hotels.fields.externalObservations",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.externalObservations ?? ""}
              onChange={(e) => {
                form.setValue("externalObservations", e.target.value);
              }}
            />
          ),
        },
      ]}
    />
  );
}
